import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import MermaidDiagram from './MermaidDiagram';

interface MarkdownRendererProps {
  children: string;
  disableMermaid?: boolean;
  className?: string;
  /**
   * Opt-in: render a colour swatch for a table cell whose sole content is a
   * bare hex value (the design_tokens Preview column — #1507). Off by default
   * so arbitrary chat/artefact markdown — where a bare `#1507` is an issue
   * reference, not a colour — is never reinterpreted as a swatch.
   */
  swatchHexCells?: boolean;
  /**
   * #1117: when given, a diagram that fails to render offers a "Regenerate diagram"
   * action which calls this with the hint message to send to the agent.
   */
  onRegenerateDiagram?: (message: string) => void;
}

// Valid CSS hex lengths only (3/4/6/8 digits) — excludes 5- and 7-digit strings
// browsers would silently drop. Only consulted when swatchHexCells is enabled.
// A code-wrapped hex (the Hex column, `#…`) arrives as a <code> element, not a
// string, so bareHexCell returns null for it and it keeps showing the value.
const BARE_HEX = /^#(?:[0-9A-Fa-f]{3,4}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/;

function bareHexCell(children: unknown): string | null {
  const parts = Array.isArray(children) ? children : [children];
  if (parts.length !== 1 || typeof parts[0] !== 'string') return null;
  const value = parts[0].trim();
  return BARE_HEX.test(value) ? value : null;
}

export default function MarkdownRenderer({
  children,
  disableMermaid = false,
  className,
  swatchHexCells = false,
  onRegenerateDiagram,
}: MarkdownRendererProps) {
  return (
    <div className={className}>
      <ReactMarkdown
        skipHtml
        disallowedElements={['img', 'video', 'audio', 'iframe', 'object']}
        remarkPlugins={[remarkGfm]}
        components={{
          h1({ children }) {
            return (
              <h1
                className="text-xl font-bold mt-6 mb-3 pb-2"
                style={{ color: 'var(--text)', borderBottom: '1px solid var(--border)', fontFamily: 'var(--font-display)' }}
              >
                {children}
              </h1>
            );
          },
          h2({ children }) {
            return (
              <h2
                className="text-lg font-semibold mt-5 mb-2"
                style={{ color: 'var(--text)', fontFamily: 'var(--font-display)' }}
              >
                {children}
              </h2>
            );
          },
          h3({ children }) {
            return (
              <h3
                className="text-base font-semibold mt-4 mb-2"
                style={{ color: 'var(--primary)', fontFamily: 'var(--font-display)' }}
              >
                {children}
              </h3>
            );
          },
          hr() {
            return <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '24px 0' }} />;
          },
          pre({ children: preChildren }) {
            // Detect mermaid fenced code block by checking child code element's className.
            // Use find to skip any string/null children; match the class token exactly.
            const rawChildren = Array.isArray(preChildren) ? preChildren : [preChildren];
            const codeEl = rawChildren.find(
              (child) => child !== null && child !== undefined && typeof child === 'object',
            );
            const codeProps = (
              codeEl as { props?: { className?: string; children?: unknown } } | null
            )?.props;

            if (
              typeof codeProps?.className === 'string' &&
              codeProps.className.split(/\s+/).includes('language-mermaid') &&
              !disableMermaid
            ) {
              const code = String(codeProps.children ?? '').trim();
              return <MermaidDiagram code={code} onRegenerate={onRegenerateDiagram} />;
            }

            return (
              <pre
                className="rounded p-3 overflow-x-auto text-sm my-4 font-mono"
                style={{
                  background: 'var(--surface-alt)',
                  color: 'var(--text)',
                  border: '1px solid var(--border)',
                }}
              >
                {preChildren}
              </pre>
            );
          },

          code({ className: codeClassName, children: codeChildren }) {
            // Inline code has no className; block code is handled by pre above
            if (!codeClassName) {
              return (
                <code
                  className="rounded px-1 py-0.5 text-sm font-mono"
                  style={{ background: 'var(--surface-alt)', color: 'var(--text)' }}
                >
                  {codeChildren}
                </code>
              );
            }
            return <code className={codeClassName}>{codeChildren}</code>;
          },

          table({ children: tableChildren }) {
            return (
              <div className="overflow-x-auto my-4">
                <table className="w-full border-collapse text-sm" style={{ border: '1px solid var(--border)' }}>
                  {tableChildren}
                </table>
              </div>
            );
          },

          thead({ children: theadChildren }) {
            return (
              <thead style={{ background: 'var(--surface-alt)' }}>
                {theadChildren}
              </thead>
            );
          },

          tr({ children: trChildren }) {
            return (
              <tr
                className="border-b"
                style={{ borderColor: 'var(--border)' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(124,58,237,0.03)')}
                onMouseLeave={e => (e.currentTarget.style.background = '')}
              >
                {trChildren}
              </tr>
            );
          },

          th({ children: thChildren }) {
            return (
              <th
                className="px-3 py-2 text-left text-xs uppercase tracking-wide font-medium"
                style={{ border: '1px solid var(--border)', background: 'var(--surface-alt)', color: 'var(--text-secondary)' }}
              >
                {thChildren}
              </th>
            );
          },

          td({ children: tdChildren }) {
            const swatchHex = swatchHexCells ? bareHexCell(tdChildren) : null;
            return (
              <td className="px-3 py-2" style={{ border: '1px solid var(--border)', color: 'var(--text)' }}>
                {swatchHex ? (
                  <span
                    data-testid="token-swatch"
                    role="img"
                    aria-label={swatchHex}
                    title={swatchHex}
                    style={{
                      display: 'inline-block',
                      width: 36,
                      height: 16,
                      backgroundColor: swatchHex,
                      border: '1px solid var(--border)',
                      borderRadius: 3,
                      verticalAlign: 'middle',
                    }}
                  />
                ) : (
                  tdChildren
                )}
              </td>
            );
          },
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
